import { Keys, locale } from 'src/i18n';
import { AppDispatch } from 'src/store';
import axios from 'src/workspace/axios';
import { DOCS_API, GENERATE_API } from './constants';
import { parseDocumentsApiResponse, parseGenerateApiResponse } from './queries';
import {
  setAgentDocGenerating,
  setAgentDocLinks,
  setAgentDocResponseMessage,
  setAgentDocsData,
  setAgentDocsLoading,
  setAgentDocsMessage,
} from './slice';
import { GenerateDocPayload } from './types';

const getAuthToken = () => {
  try {
    const { hash } = window.location;
    const queryString = hash.split('?')?.[1] || '';
    const params = new URLSearchParams(queryString);
    return params.get('auth_token');
  } catch (e) {
    return '';
  }
};

const getRequestErrorMessage = (
  error: any,
  fallbackMessage: string,
): string => {
  const payload = error?.response?.data;
  if (payload?.status === 'error' || payload?.success === false) {
    return (
      payload?.errorMessage || payload?.message || payload?.errors?.[0]?.message
    );
  }
  return fallbackMessage;
};

export const fetchAgentDocuments = () => async (dispatch: AppDispatch) => {
  try {
    dispatch(setAgentDocsLoading(true));
    dispatch(setAgentDocsMessage(''));

    const response = await axios.get(DOCS_API, {
      headers: { 'x-vymo-auth-token': getAuthToken() },
    });
    const { documents, errorMessage, emptyMessage } = parseDocumentsApiResponse(
      response?.data,
    );

    if (errorMessage) {
      dispatch(setAgentDocsData({ documents: [], docsMessage: errorMessage }));
      return;
    }

    dispatch(
      setAgentDocsData({
        documents,
        docsMessage: emptyMessage ?? '',
      }),
    );
  } catch (error: any) {
    dispatch(
      setAgentDocsData({
        documents: [],
        docsMessage: getRequestErrorMessage(
          error,
          locale(Keys.AGENT_DOCS_FETCH_ERROR),
        ),
      }),
    );
  } finally {
    dispatch(setAgentDocsLoading(false));
  }
};

export const generateAgentDocumentLinks =
  ({ docId, filters }: GenerateDocPayload) =>
  async (dispatch: AppDispatch) => {
    try {
      dispatch(setAgentDocGenerating({ docId, loading: true }));
      dispatch(setAgentDocResponseMessage({ docId, message: '' }));

      const response = await axios.post(
        GENERATE_API,
        {
          docId,
          filters,
        },
        {
          headers: {
            'x-vymo-auth-token': getAuthToken(),
          },
        },
      );
      const { links, errorMessage, emptyMessage } = parseGenerateApiResponse(
        response?.data,
      );

      if (errorMessage || emptyMessage) {
        dispatch(
          setAgentDocResponseMessage({
            docId,
            message:
              errorMessage || emptyMessage || locale(Keys.AGENT_DOCS_NO_LINKS),
          }),
        );
        return;
      }

      dispatch(setAgentDocLinks({ docId, links }));
    } catch (error: any) {
      dispatch(
        setAgentDocResponseMessage({
          docId,
          message: getRequestErrorMessage(
            error,
            locale(Keys.AGENT_DOCS_GENERATE_ERROR),
          ),
        }),
      );
    } finally {
      dispatch(setAgentDocGenerating({ docId, loading: false }));
    }
  };

const onActivate = ({ dispatch }) => {
  dispatch(fetchAgentDocuments());
};

const onDeactivate = () => {};

export default { onActivate, onDeactivate };
